// Curated list of legendary scenic highways. When a route runs a meaningful
// stretch of one of these, we treat it as the *most pleasant* option and
// suppress the usual highway penalties for that portion of the drive.
//
// RULE (Morgan): a road with NO coordinates must never match purely by NAME.
// Name/ref patterns alone caused global false positives — a small "Ring Road"
// loop in Connecticut matched Iceland's Ring Road; ref "1" matched any road
// numbered 1. So every epic highway carries a `bbox` (its real location), and a
// match counts ONLY when the route ALSO passes through that box. No bbox, or no
// route coordinates supplied → no match. Geography is the gate; the name is just
// the secondary signal.

import type { LngLat } from "@/utils/polyline";

export interface BBox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

export interface EpicHighway {
  /** Display label, used in evidence/explanation text. */
  label: string;
  /** Where this road actually is. A name match outside this box is ignored. */
  bbox: BBox;
  /** Regexes tested against Mapbox step `name` (the street name). */
  namePatterns?: RegExp[];
  /** Regexes tested against Mapbox step `ref` (route shield, e.g. "CA 1"). */
  refPatterns?: RegExp[];
}

export const EPIC_HIGHWAYS: EpicHighway[] = [
  {
    label: "Pacific Coast Highway (CA-1)",
    bbox: { minLng: -124.6, maxLng: -116.8, minLat: 32.5, maxLat: 42.1 },
    namePatterns: [/pacific coast hwy/i, /cabrillo hwy/i, /shoreline hwy/i],
    refPatterns: [/^CA[\s-]?1$/i, /^US[\s-]?101$/i, /^SR[\s-]?1$/i],
  },
  {
    label: "Route 66",
    bbox: { minLng: -118.6, maxLng: -87.5, minLat: 33.9, maxLat: 42.1 },
    namePatterns: [/route 66/i, /historic 66/i], refPatterns: [/^US[\s-]?66$/i],
  },
  { label: "Blue Ridge Parkway", bbox: { minLng: -84.2, maxLng: -78.4, minLat: 35.0, maxLat: 38.7 }, namePatterns: [/blue ridge pkwy/i, /blue ridge parkway/i] },
  { label: "Skyline Drive", bbox: { minLng: -78.95, maxLng: -78.1, minLat: 38.0, maxLat: 39.05 }, namePatterns: [/skyline dr/i] },
  { label: "Going-to-the-Sun Road", bbox: { minLng: -114.5, maxLng: -113.3, minLat: 48.4, maxLat: 49.0 }, namePatterns: [/going[- ]to[- ]the[- ]sun/i] },
  { label: "Overseas Highway", bbox: { minLng: -82.2, maxLng: -80.3, minLat: 24.4, maxLat: 25.5 }, namePatterns: [/overseas hwy/i] },
  { label: "Beartooth Highway", bbox: { minLng: -110.1, maxLng: -108.9, minLat: 44.8, maxLat: 45.5 }, namePatterns: [/beartooth/i], refPatterns: [/^US[\s-]?212$/i] },
  { label: "Hana Highway", bbox: { minLng: -156.8, maxLng: -155.9, minLat: 20.6, maxLat: 21.1 }, namePatterns: [/hana hwy/i], refPatterns: [/^HI[\s-]?360$/i] },
  { label: "Million Dollar Highway", bbox: { minLng: -108.1, maxLng: -107.5, minLat: 37.7, maxLat: 38.2 }, namePatterns: [/million dollar/i], refPatterns: [/^US[\s-]?550$/i] },
  { label: "Tail of the Dragon (US-129)", bbox: { minLng: -84.2, maxLng: -83.6, minLat: 35.3, maxLat: 35.7 }, namePatterns: [/tail of the dragon/i], refPatterns: [/^US[\s-]?129$/i] },
  { label: "Trail Ridge Road", bbox: { minLng: -106.0, maxLng: -105.4, minLat: 40.2, maxLat: 40.55 }, namePatterns: [/trail ridge/i] },
  { label: "Icefields Parkway", bbox: { minLng: -118.2, maxLng: -115.9, minLat: 51.3, maxLat: 52.6 }, namePatterns: [/icefields pkwy/i, /icefields parkway/i] },
  { label: "Sea-to-Sky Highway", bbox: { minLng: -123.4, maxLng: -122.7, minLat: 49.3, maxLat: 50.2 }, namePatterns: [/sea[- ]to[- ]sky/i] },
  { label: "Cabot Trail", bbox: { minLng: -61.2, maxLng: -60.2, minLat: 46.4, maxLat: 47.1 }, namePatterns: [/cabot trail/i] },
  { label: "Great Ocean Road", bbox: { minLng: 142.4, maxLng: 144.6, minLat: -39.1, maxLat: -38.2 }, namePatterns: [/great ocean rd/i, /great ocean road/i] },
  { label: "Ring Road (Iceland)", bbox: { minLng: -24.7, maxLng: -13.3, minLat: 63.2, maxLat: 66.7 }, namePatterns: [/hringvegur/i, /ring road/i], refPatterns: [/^1$/] },
  { label: "Amalfi Coast Road (SS163)", bbox: { minLng: 14.4, maxLng: 15.0, minLat: 40.5, maxLat: 40.7 }, namePatterns: [/amalfitana/i], refPatterns: [/^SS[\s-]?163$/i] },
  { label: "Stelvio Pass", bbox: { minLng: 10.2, maxLng: 10.7, minLat: 46.3, maxLat: 46.7 }, namePatterns: [/stelvio/i, /passo dello stelvio/i] },
  { label: "Transfăgărășan", bbox: { minLng: 24.3, maxLng: 24.9, minLat: 45.4, maxLat: 45.8 }, namePatterns: [/transf[aă]g[aă]r[aă][sș]an/i], refPatterns: [/^DN[\s-]?7C$/i] },
  { label: "Romantic Road", bbox: { minLng: 9.4, maxLng: 10.9, minLat: 47.4, maxLat: 49.9 }, namePatterns: [/romantische stra[sß]e/i, /romantic road/i] },
  { label: "Atlantic Road", bbox: { minLng: 6.9, maxLng: 8.0, minLat: 62.9, maxLat: 63.2 }, namePatterns: [/atlanterhavsvegen/i, /atlantic ocean road/i] },
  { label: "North Coast 500", bbox: { minLng: -5.6, maxLng: -2.9, minLat: 57.0, maxLat: 58.8 }, namePatterns: [/north coast 500/i, /\bnc500\b/i] },
  // Scenic NY parkways — divided 2-lane woodland parkways that ARE the pleasant
  // drive, not a highway slog (Morgan: "not all highways are the same"). The
  // Taconic especially is the de-facto pleasant route for east-Hudson trips.
  { label: "Taconic State Parkway", bbox: { minLng: -73.95, maxLng: -73.5, minLat: 41.0, maxLat: 42.5 }, namePatterns: [/taconic state (pkwy|parkway)/i, /\btaconic pkwy\b/i] },
  { label: "Bear Mountain State Parkway", bbox: { minLng: -74.0, maxLng: -73.85, minLat: 41.25, maxLat: 41.42 }, namePatterns: [/bear mountain state parkway/i] },
  { label: "Palisades Interstate Parkway", bbox: { minLng: -74.05, maxLng: -73.9, minLat: 40.95, maxLat: 41.35 }, namePatterns: [/palisades (interstate )?parkway/i] },
];

function bboxOfCoords(coords: LngLat[]): BBox | null {
  if (!coords || coords.length === 0) return null;
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLng, maxLng, minLat, maxLat };
}

function bboxIntersects(a: BBox, b: BBox): boolean {
  return a.minLng <= b.maxLng && a.maxLng >= b.minLng && a.minLat <= b.maxLat && a.maxLat >= b.minLat;
}

/** Returns total meters of the route that ran on a known epic highway, with the
 *  labels matched (deduped). A match requires BOTH a name/ref hit AND the route
 *  passing through the highway's bbox — so a same-named road far from the real
 *  highway can never false-match. Without `routeCoords`, nothing matches. */
export function detectEpicHighways(
  roadSegments: { name?: string; ref?: string; meters: number }[],
  routeCoords?: LngLat[],
): { meters: number; labels: string[] } {
  const routeBbox = routeCoords ? bboxOfCoords(routeCoords) : null;
  // RULE: no route geography → no name-only matching.
  if (!routeBbox) return { meters: 0, labels: [] };
  const inRegion = EPIC_HIGHWAYS.filter((e) => bboxIntersects(e.bbox, routeBbox));
  if (inRegion.length === 0) return { meters: 0, labels: [] };

  let meters = 0;
  const labels = new Set<string>();
  for (const seg of roadSegments) {
    const name = seg.name ?? "";
    const ref = seg.ref ?? "";
    if (!name && !ref) continue;
    // REFS ARE LISTS, AND THEY CARRY SUFFIXES (Aug 10, from the [epic] log on
    // San Jose → Flagstaff — measured, not assumed):
    //     US 66 Hist                        ← ref, with a suffix
    //     I 15 BUS; CA 18; US 66 Hist       ← several refs in one field
    //     AZ 66; US 66 Hist                 ← ditto
    // and the NAMES on those same segments are "D Street", "Hualapai Veterans
    // Highway", "East Lewis Avenue" — nothing a /route 66/ pattern could ever
    // catch. So the ref is the only reliable signal for Route 66, and every
    // anchored refPattern (`/^US[\s-]?66$/`) was guaranteed to miss it twice
    // over: once on the "; " list, once on the " Hist" suffix.
    //
    // Splitting here rather than loosening each pattern keeps the patterns
    // readable and fixes every epic road at once — PCH's /^CA[\s-]?1$/ has the
    // identical problem on any segment tagged "CA 1; SR 1".
    const refTokens = ref
      .split(";")
      .map((r) => r.trim())
      // Drop the qualifier so `US 66 Hist` matches a `US 66` pattern. "Historic
      // Route 66" and "US 66" are the same road; OSM just labels the historic
      // alignment. BUS/TRUCK/ALT are different alignments of the same route and
      // are equally the road a driver would call it.
      .map((r) => r.replace(/\s+(hist(oric)?|bus(iness)?|truck|alt(ernate)?|byp(ass)?|spur|loop)$/i, "").trim())
      .filter(Boolean);
    for (const epic of inRegion) {
      const nameHit = epic.namePatterns?.some((re) => re.test(name)) ?? false;
      const refHit =
        epic.refPatterns?.some((re) => re.test(ref) || refTokens.some((t) => re.test(t))) ?? false;
      if (nameHit || refHit) {
        meters += seg.meters;
        labels.add(epic.label);
        break;
      }
    }
  }
  // WHAT THE ROADS ARE ACTUALLY CALLED (rule 19: measure before writing the fix).
  //
  // Aug 10: a San Jose → Flagstaff run put "Takes I 15 North and US 66 Hist" on
  // the card while this function returned 0 metres, so the Pleasant card read
  // "Mostly on main roads" for a route on Historic Route 66. The Taconic does
  // the same on Hudson Valley trips.
  //
  // The patterns here were written from how a PERSON says a road name, not from
  // what the provider returns. Route 66's three patterns all miss "US 66 Hist":
  //   /route 66/i    → the name says "US 66", not "Route 66"
  //   /historic 66/i → the words are the other way round
  //   /^US[\s-]?66$/ → anchored, so " Hist" breaks it
  // Writing a second set of regexes from the same guesswork would repeat the
  // mistake. This prints the real strings so they can be written from evidence.
  //
  // Diagnostic only — remove once the patterns are corrected and a test covers
  // the actual names.
  if (meters === 0 && inRegion.length > 0) {
    const named = roadSegments
      .filter((s) => s.name || s.ref)
      .sort((a, b) => b.meters - a.meters)
      .slice(0, 12)
      .map((s) => `${s.name ?? "?"}|${s.ref ?? "?"}@${Math.round(s.meters / 1000)}km`);
    // eslint-disable-next-line no-console
    console.log(
      `[epic] NO MATCH in region [${inRegion.map((e) => e.label).join(", ")}] — ` +
        `longest named segments: ${named.join(" · ") || "(no segment carries a name or ref)"}`,
    );
  }
  return { meters, labels: Array.from(labels) };
}
